import React, { useState, useEffect, useRef, useCallback } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Breadcrumb from 'tbk-components/src/components/Breadcrumb';
import { IVideo, IVideoModalProps } from '../VideoModal';
import AnimatedArcs from '../AnimatedArcs';
// Note, I choice to use the Api service directly here as opposed to withAppContext for two reasons:
//   1. withAppContext causes the loading spinner to be the thing that gets saved to the DB, which is bad for searching/SEO.
//   2. withAppContext changes the content from what's on the site already, causing "Attempt Block Recovery" warnings.
import Api from 'tbk-components/src/services/Api';
import Icon from 'tbk-components/src/components/Icon';
import Carousel, { CarouselItem } from 'tbk-components/src/components/Carousel';
import AdvancedMedia from '../AdvancedMedia';
import FloatingCTA, { IFloatingCTA } from '../FloatingCTA';
import FormLightboxButton, {
	IFormLightboxButtonProps,
} from '../FormLightboxButton';

export interface IHeroContent {
	heading: string;
	/**
	 * Eyebrow heading
	 */
	eyebrow: string;
	/**
	 * Text
	 * @control richtext
	 */
	content?: string;
	cta: ILink;
	lightboxCTA?: IFormLightboxButtonProps;
	hideBreadcrumbs?: boolean;
	/**
	 * @noUI
	 */
	breadcrumbs?: ILink[];
	inverted?: boolean;
}

export interface IHeroMedia {
	image?: IImage;
	video?: IVideoModalProps;
}

export interface IHeroProps extends IBaseElementProps {
	content?: IHeroContent;

	mediaImage?: IImage;
	/**
	 * Note: Uploading a video will limit some styling options.
	 */
	mediaVideo?: IVideo;
	/**
	 * @noUI
	 */
	mediaGallery?: IHeroMedia[];
	/**
	 * @noUI
	 */
	floatingCTA?: IFloatingCTA;
	textAlignment?: 'left' | 'center';
	mediaPosition?: 'split' | 'wallpaper';
	backgroundOverlay?: 'solid' | 'gradient' | 'themed';
	inverted?: boolean;
	/**
	 * @noUI
	 * This is only used to turn off PHP Hydration
	 */
	disableDynamicBreadcrumbs?: boolean;
}

/**
 * Calian Hero
 *
 * Solid, gradient & themed options with text alignment, and image support.
 * The image can be positioned as a split image or as a wallpaper image.
 * The breadcrumbs will be filled in automatically on the front end.  What you see in the backend is just a placeholder.
 *
 * @block
 * @icon cover-image
 */
const Hero: React.FC<IHeroProps> = ({
	id,
	className,
	classNames = [],
	content,
	mediaImage,
	mediaVideo,
	mediaGallery = [],
	floatingCTA,
	textAlignment = 'left',
	mediaPosition = 'split',
	backgroundOverlay = 'solid',
	inverted = false,
	disableDynamicBreadcrumbs = false,
}) => {
	const [breadcrumbs, setBreadcrumbs] = useState<ILink[]>(
		content?.breadcrumbs,
	);
	const heroRef = useRef(null);

	//There is some old content on WP Admin with `blue` overlays. Will fix this for Content automatically.
	// @ts-ignore
	backgroundOverlay =
		// @ts-ignore
		backgroundOverlay == 'blue' ? 'themed' : backgroundOverlay;

	if (mediaGallery && mediaGallery.length > 0) {
		mediaPosition = 'split';
	}

	// This effect is to make the breadcrumb sync up in the context of a gutenberg editor.  It's not used on the front end.
	// Update: the mutation observer would sometimes make the component unrecoverable, this rewrite fixes that.
	// we're adding a check in case title of post is not available yet. and then we're rerunning the check after 100ms until it loads.
	useEffect(() => {
		if (document?.body.classList.contains('wp-admin')) {
			setBreadcrumbs([
				{
					label: 'Loading breadcrumbs from server...',
					href: '#',
				},
			]);

			// we're defining the observer here so we can still clean it later on dismount.
			let observer;

			const checkTitleElAndObserve = () => {
				const titleEl = document.querySelector('.editor-post-title');

				if (titleEl) {
					const updateLastBreadcrumb = () => {
						setBreadcrumbs((breadcrumbs) => {
							const last = breadcrumbs.pop();
							return [
								...breadcrumbs,
								{
									...last,
									label: titleEl.textContent,
								},
							];
						});
					};

					Api.prepare().then(() => {
						// @ts-ignore
						Api.getBreadcrumbs(document.getElementById('post_ID').value)
							.then(setBreadcrumbs)
							.then(() => {
								updateLastBreadcrumb(); // in case it's changed in the editor
							});
					});

					// Now we're going to set up an observer on the title element so that we can update the breadcrumbs
					// when the title changes.
					const config = { characterData: true, subtree: true };

					// Callback function to execute when mutations are observed
					const callback = (mutationList, observer) => {
						for (const mutation of mutationList) {
							if (mutation.type === 'characterData') {
								updateLastBreadcrumb();
							}
						}
					};

					// Create an observer instance linked to the callback function
					observer = new MutationObserver(callback);

					// Start observing the target node for configured mutations
					observer.observe(titleEl, config);
				} else {
					setTimeout(checkTitleElAndObserve, 100);
				}
			};

			checkTitleElAndObserve();

			return () => {
				if (observer) {
					observer.disconnect();
				}
			};
		}
	}, []);

	const videoRef = useRef(null);
	const [videoPlaying, setPlayState] = useState(true);
	const videoPartial = mediaVideo ? (
		<video
			ref={videoRef}
			className="calian-hero__video position-absolute h-100 w-100 start-0 top-0 z-2"
			autoPlay
			muted
			loop
			src={mediaVideo.src}
		/>
	) : null;

	useEffect(() => {
		if (!videoRef.current) return;
		videoPlaying ? videoRef.current.play() : videoRef.current.pause();
	}, [videoPlaying]);

	const createWhiteSpace = useCallback((el) => {
		const spanEl = el.querySelector('.calian-hero > span') as HTMLElement;
		const b = el.querySelector('.calian-hero__breadcrumbs') as HTMLElement;

		if (b) {
			const bt = b.offsetTop + 1;
			const bh = b.offsetHeight;

			spanEl.setAttribute(
				'style',
				`position: absolute; top: ${bt}px; width: 100%; height: calc(${bh}px + 1rem);`,
			);
		} else {
			spanEl.setAttribute(
				'style',
				`position: absolute; bottom: 0; width: 100%; height: 5rem;`,
			);
		}
	}, []);

	useEffect(() => {
		const setSpanPos = () => {
			if (!heroRef.current) return;

			const hero = heroRef.current as HTMLElement;

			if (
				(hero.classList.contains('overlay-blue'),
				hero.classList.contains('overlay-themed'),
				hero.classList.contains('has-split-image'))
			) {
				createWhiteSpace(hero);
			}
		};

		setSpanPos();
		window.addEventListener('resize', setSpanPos);

		const targetNode = document.body;
		const config = { attributes: true, attributeFilter: ['class'] };
		const callback = function (mutationsList, observer) {
			for (const mutation of mutationsList) {
				if (
					mutation.type === 'attributes' &&
					mutation.attributeName === 'class'
				) {
					const currentClasses = targetNode.classList;
					if (currentClasses.contains('has-sub-nav')) {
						setSpanPos();
					}
				}
			}
		};
		const observer = new MutationObserver(callback);

		observer.observe(targetNode, config);

		return () => {
			window.removeEventListener('resize', setSpanPos);
			observer.disconnect();
		};
	}, []);

	const computedMediaPosition = mediaVideo ? 'wallpaper' : mediaPosition;
	const imageWallpaperStyles =
		computedMediaPosition == 'wallpaper'
			? ['position-absolute', 'opacity-25', 'top-0', 'start-0', 'p-0']
			: [];
	const indent = true; //Will eventually be used to determine if the hero should have extra spacing for the main navigation overlay.

	const generatedSingleImage = mediaImage || mediaGallery?.[0]?.image || null;
	return (
		<section
			id={id}
			className={
				className ||
				cn(
					'calian-hero',
					'd-flex',
					'h-100',
					'position-relative',
					'overflow-hidden',
					indent && 'nav-indent',
					...classNames,
					`media-position-${mediaPosition}`,
					(mediaImage || mediaGallery?.length > 0) &&
						mediaPosition === 'split' &&
						'has-split-image pb-5 pb-lg-0',
					`overlay-${backgroundOverlay}`,
					content?.hideBreadcrumbs && 'breadcrumbs-hidden',
				)
			}
			ref={heroRef}
		>
			<div className="container">
				{videoPartial && (
					<a
						href="#"
						className="calian-hero__play-pause d-flex align-items-center text-decoration-none d-block m-2 p-2 position-absolute z-3 text-left text-white"
						onClick={(e) => {
							e.preventDefault();
							setPlayState(!videoPlaying);
						}}
					>
						<Icon name={videoPlaying ? 'pause' : 'play'} />
						<span className="d-block ms-2">
							{videoPlaying ? 'Pause' : 'Play'}
						</span>
					</a>
				)}
				<div className="row">
					<div
						className={cn(
							'calian-hero__content',
							'd-flex',
							'col-lg',
							'flex-column',
							'pb-3',
							'justify-content-center',
							textAlignment == 'center' &&
								'align-items-center' &&
								'mx-auto',
							mediaImage && mediaPosition !== 'split' && 'h-100',
							!content?.hideBreadcrumbs &&
								content?.breadcrumbs?.length > 0 &&
								'has-breadcrumbs',
							`text-${textAlignment}`,
						)}
					>
						<div className={cn('calian-hero__copy-wrapper')}>
							{content?.eyebrow && (
								<p
									className={cn(
										'h4 calian-hero__eyebrow',
										(backgroundOverlay === 'themed' ||
											mediaVideo) &&
											'text-white',
									)}
								>
									{content.eyebrow}
								</p>
							)}
							{content?.heading && (
								<h1
									className={cn(
										'calian-hero__heading',
										(backgroundOverlay === 'themed' ||
											mediaVideo) &&
											'text-white',
									)}
								>
									{content.heading}
								</h1>
							)}
							{content?.content && (
								<div
									className={cn(
										'calian-hero__text',
										(backgroundOverlay === 'themed' ||
											mediaVideo) &&
											'text-white',
										'mt-2 mb-4',
									)}
									dangerouslySetInnerHTML={{
										__html: content.content,
									}}
								/>
							)}
							{content?.cta?.label && !floatingCTA && (
								<Button
									className={cn(
										`btn`,
										inverted
											? 'btn-secondary'
											: backgroundOverlay === 'themed'
											? `btn-light`
											: `btn-primary`,
										'mb-4',
										content?.content && 'mt-3',
									)}
									{...content.cta}
								/>
							)}
							{floatingCTA && <FloatingCTA {...floatingCTA} />}
							{content?.lightboxCTA?.formLightboxButton?.label
								?.length > 0 && (
								<FormLightboxButton {...content.lightboxCTA} />
							)}
						</div>
						{!content?.hideBreadcrumbs && breadcrumbs?.length > 0 && (
							<div
								className={cn(
									'calian-hero__breadcrumbs',
									'mt-auto',
									'mb-0',
									mediaImage && mediaPosition === 'split'
										? 'pt-lg-4 pt-3 pb-lg-0 pb-md-3 pb-1'
										: 'pt-5',
									((backgroundOverlay == 'themed' &&
										(computedMediaPosition ===
											'wallpaper' ||
											!mediaImage)) ||
										mediaVideo) &&
										'text-white',
								)}
							>
								<Breadcrumb links={breadcrumbs} />
							</div>
						)}
					</div>
					{(mediaImage?.src || mediaGallery?.length > 0 || videoPartial !== null) && (
						<div
							className={cn(
								'calian-hero__media',
								'col-md',
								'w-100',
								'h-100',
								mediaImage &&
									mediaPosition === 'split' &&
									indent &&
									'pt-lg-5 mt-lg-3',
								`media-style-${computedMediaPosition}`,
								...imageWallpaperStyles,
								mediaGallery.length > 0 &&
									'calian-hero__media-gallery pt-lg-5 mt-lg-3',
							)}
						>
							<div
								className={cn(
									'media-wrapper',
									computedMediaPosition !== 'wallpaper' &&
										'ps-lg-5 pb-3 pb-lg-0',
									'ml-auto',
									'mr-0',
									'w-100',
									'h-100',
								)}
							>
								{videoPartial}
								{(mediaImage ||
									(mediaGallery &&
										mediaGallery?.length === 1)) && (
									<div className="advanced-image w-100 h-100">
										<Image
											className="calian-hero__image"
											{...generatedSingleImage}
										/>
									</div>
								)}
								{
									// the image gallery is for displaying product images only
									// it is not open for editing in WP admin UI
								}
								{mediaGallery && mediaGallery.length > 1 && (
									<Carousel
										showArrows={true}
										showBullets={true}
										onMount={() => {
											if (heroRef.current) {
												const hero =
													heroRef.current as HTMLElement;
												createWhiteSpace(hero);
											}
										}}
									>
										{mediaGallery.map((media, index) => {
											return (
												<CarouselItem key={index}>
													{media.image && (
														<AdvancedMedia
															image={media.image}
															size="1:1"
														/>
													)}
													{media.video && (
														<AdvancedMedia
															video={media.video}
															size="1:1"
														/>
													)}
												</CarouselItem>
											);
										})}
									</Carousel>
								)}
							</div>
						</div>
					)}
				</div>
			</div>
			{backgroundOverlay !== 'themed' && !mediaImage && !mediaGallery && (
				<AnimatedArcs
					arcsStyle={'Component - Two Lines'}
					arcsPosition="Right"
					arcOneColor={'var(--bs-light)'}
					arcOneStrokeWidth={4}
					arcTwoColor={'var(--bs-white)'}
					arcTwoStrokeWidth={3}
					animationDelay={50}
					animationOffset={150}
				/>
			)}
			<div
				className={cn(
					'calian-hero__overlay',
					`overlay-${videoPartial ? 'video' : backgroundOverlay}`,
					'position-absolute',
					'w-100',
					(!mediaImage ||
						computedMediaPosition == 'wallpaper' ||
						!mediaGallery ||
						mediaGallery?.length === 0) &&
						'h-100',
					'top-0',
					'start-0',
					(mediaImage || mediaGallery?.length > 0) && 'has-image',
					mediaVideo && 'has-video',
				)}
			></div>
			<span></span>
		</section>
	);
};

export default Hero;
